import React, { useState, useMemo, useEffect } from 'react';
import { X, Plus, AlertCircle, Scale, Info } from 'lucide-react';
import { formatTimeAgo, CollapsibleSection, NumCell } from './CombativesShell';
import { useProfile } from '../../context/ProfileContext';
import MediaCaptureMenu from '../MediaCaptureMenu';
import PasteUrlInput from '../PasteUrlInput';
import AttachmentTile from '../AttachmentTile';
import { fileToPayload, urlToPayload } from '../../lib/mediaCapture';
import MultiSelectPills from './MultiSelectPills';
import {
  BOXING_STANCES,
  BOXING_CLASS_FORMATS,
  BOXING_FOCUS_MODES,
  BOXING_SPARRING_TRIGGERS,
  BOXING_MASTERY_LEVELS,
  BOXING_FELT_SCALE,
  BOXING_GROUPS,
  boxingSubgroupsForGroup,
  boxingTechniquesFor,
  BOXING_GLOVE_OPTIONS,
  BOXING_PARTNER_LEVELS,
  BOXING_COACH_TOPICS,
  BOXING_MATCH_RESULTS,
  BOXING_CUT_METHODS,
  BOXING_HYDRATION,
  BOXING_WEIGHT_FLAGS,
  BOXING_WEIGHT_REFERENCES,
} from '../../data/combativesBoxing';

const DRAFT_KEY = 'enferno_boxing_session_draft_v1';


// -- Factory helpers (mirror Wrestling shape exactly so the future
//    `<StrikingJournalDialog>` refactor is mechanical) --
const newTechnique = () => ({
  group: 'offense',
  subgroup: '',
  name: '',
  reps: '',
  mastery: '',
  note: '',
});
const newCombo = () => ({ sequence: '', label: '', reps: '', notes: '' });
const newCoachNote = () => ({ topic: '', note: '' });
// W/L/Draw enum + free-text `finish` per founder.
const newMatch = () => ({ opponent: '', result: 'win', finish: '', notes: '' });
const newInjury = () => ({ area: '', severity: 1, note: '' });
const newRoundNote = () => ({ roundNum: '', note: '' });

const newSparring = () => ({
  roundsCompleted: '',
  roundLengthSec: 180,
  gloveWeightOz: '',
  partnerExperienceLevel: '',
  intensityRating: 3,
  cleanLandsScored: '',
  cleanLandsConceded: '',
  knockdownsScored: '',
  knockdownsConceded: '',
  rounds: [],  // optional per-round notes (on-demand)
  note: '',
});

const newWeight = () => ({
  scaleWeightLb: '',
  targetClassLb: '',
  daysToWeighIn: '',
  cutMethod: '',
  hydrationStatus: '',
  notes: '',
});

/**
 * BoxingLogDialog — rich session journal for Boxing. Routed in
 * Training.jsx when art.id === 'boxing'. Schema and copy locked
 * per `/app/memory/BOXING_RESEARCH.md`, founder-approved.
 *
 * Boxing-specific elements vs. shared Wrestling/MT scaffold:
 *   - Session-level `stance` (orthodox default), `gloveWeightOz`,
 *     `partnerExperienceLevel`.
 *   - Sparring is rounds-based (rounds + length + intensity + clean
 *     lands + knockdowns + optional per-round notes).
 *   - Structured `combinations` array (sequence + label + reps).
 *   - Matches have W/L/Draw + optional free-text `finish` string
 *     (TKO R2, decision, etc.) — no enum, per founder.
 *   - No conditioning block — boxers track that on the Endurance "Bag
 *     Work" surface.
 *   - Weight block present but COLLAPSED BY DEFAULT (most recreational
 *     boxers won't open it).
 */
const BoxingLogDialog = ({ art, onClose, onSave, initialData = null }) => {
  const { training } = useProfile();
  const isEditMode = !!initialData;

  const todayLocal = useMemo(() => {
    const d = new Date();
    const tzOff = d.getTimezoneOffset() * 60000;
    return new Date(d - tzOff).toISOString().slice(0, 10);
  }, []);

  // Glove-weight prefill: pull from the most recent boxing sparring session.
  const gloveWeightDefault = useMemo(() => {
    const prior = (training?.sessions || [])
      .filter((s) => s.artId === 'boxing' && s.sparring?.gloveWeightOz)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    return prior[0]?.sparring?.gloveWeightOz || '';
  }, [training?.sessions]);

  const initialFormState = () => ({
    date: initialData?.date ? new Date(initialData.date).toISOString().slice(0, 10) : todayLocal,
    duration: initialData?.duration ?? '',
    stance: initialData?.stance || 'orthodox',
    classFormat: initialData?.classFormat || 'mitt',
    focusModes: initialData?.focusModes || ['Drilling'],
    intent: initialData?.intent || '',
    techniques: initialData?.techniques?.length ? initialData.techniques : [newTechnique()],
    combinations: initialData?.combinations || [],
    sparring: initialData?.sparring || null,
    coachFeedback: initialData?.coachFeedback || [],
    weight: initialData?.weight || null,
    matches: initialData?.matches || [],
    injuryConcerns: initialData?.injuryConcerns || [],
    reflection: initialData?.reflection || '',
    notes: initialData?.notes || '',
    attachments: initialData?.attachments || [],
  });

  const [form, setForm] = useState(initialFormState);
  const [restoredFromDraft, setRestoredFromDraft] = useState(null);
  const [error, setError] = useState('');

  // --- Draft autosave (localStorage only, never network — same posture as Wrestling) ---
  useEffect(() => {
    if (isEditMode) return;
    let parsed = null;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) parsed = JSON.parse(raw);
    } catch { /* ignore */ }
    if (parsed?.savedAt && parsed?.form) {
      Promise.resolve().then(() => {
        setForm(parsed.form);
        setRestoredFromDraft(parsed.savedAt);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEditMode) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ savedAt: new Date().toISOString(), form }));
      } catch { /* ignore */ }
    }, 600);
    return () => clearTimeout(t);
  }, [form, isEditMode]);

  const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ } };

  // --- Derived: sparring trigger + match-block trigger ---
  const showSparring = useMemo(
    () => form.focusModes.some((m) => BOXING_SPARRING_TRIGGERS.includes(m))
      || form.classFormat === 'sparring'
      || form.classFormat === 'match_bout'
      || form.classFormat === 'tournament',
    [form.focusModes, form.classFormat],
  );
  const showMatchBlock = form.classFormat === 'match_bout' || form.classFormat === 'tournament';

  // --- Anti-shame weight guardrails (same posture as wrestling) ---
  const weightFlag = useMemo(() => {
    if (!form.weight) return null;
    const w = form.weight;
    if (w.hydrationStatus === 'red') {
      return { tone: 'urgent', message: "You flagged red hydration. Stop cutting today — get fluids and electrolytes. We're a notebook, not a doctor; if you feel off, talk to your coach or call a physician." };
    }
    const priorSessions = (training?.sessions || [])
      .filter((s) => s.artId === 'boxing' && s.weight?.scaleWeightLb)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    if (priorSessions.length > 0 && w.scaleWeightLb) {
      const prior = priorSessions[0];
      const priorWeight = parseFloat(prior.weight.scaleWeightLb);
      const today = parseFloat(w.scaleWeightLb);
      const drop = priorWeight - today;
      const days = Math.max(1, (new Date(form.date) - new Date(prior.date)) / 86_400_000);
      const dropPerDay = drop / days;
      if (dropPerDay >= BOXING_WEIGHT_FLAGS.rapidDropLbsPerDay) {
        return { tone: 'caution', message: `That's a ${drop.toFixed(1)}lb drop in ~${Math.round(days)} day${days === 1 ? '' : 's'}. How are you feeling? Safe-cut floors are ~1.5% body weight per week. Make sure your coach is in the loop.` };
      }
    }
    if (w.hydrationStatus === 'amber') {
      return { tone: 'caution', message: 'Amber hydration — keep an eye on it. Pale urine and clear thinking are your two best signals.' };
    }
    return null;
  }, [form.weight, training?.sessions, form.date]);

  // --- Mutation helpers ---
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTechAt = (i, patch) => setForm((f) => ({
    ...f, techniques: f.techniques.map((t, idx) => idx === i ? { ...t, ...patch } : t),
  }));
  const removeTechAt = (i) => setForm((f) => ({ ...f, techniques: f.techniques.filter((_, idx) => idx !== i) }));
  const setComboAt = (i, patch) => setForm((f) => ({
    ...f, combinations: f.combinations.map((c, idx) => idx === i ? { ...c, ...patch } : c),
  }));
  const setCoachAt = (i, patch) => setForm((f) => ({
    ...f, coachFeedback: f.coachFeedback.map((c, idx) => idx === i ? { ...c, ...patch } : c),
  }));
  const setMatchAt = (i, patch) => setForm((f) => ({
    ...f, matches: f.matches.map((m, idx) => idx === i ? { ...m, ...patch } : m),
  }));
  const setInjuryAt = (i, patch) => setForm((f) => ({
    ...f, injuryConcerns: f.injuryConcerns.map((j, idx) => idx === i ? { ...j, ...patch } : j),
  }));

  // --- Attachments ---
  const addAttachmentFiles = async (files) => {
    const payloads = await Promise.all(files.map((f) => fileToPayload(f)));
    setForm((f) => ({ ...f, attachments: [...f.attachments, ...payloads] }));
  };
  const addAttachmentUrl = async (url, kind) => {
    const payload = await urlToPayload(url, kind);
    if (payload) setForm((f) => ({ ...f, attachments: [...f.attachments, payload] }));
  };
  const removeAttachment = (idx) => setForm((f) => ({ ...f, attachments: f.attachments.filter((_, i) => i !== idx) }));

  // --- Save ---
  const handleSave = () => {
    setError('');
    if (!form.duration || form.duration <= 0) {
      setError('Please add a duration (minutes).');
      return;
    }
    const isToday = form.date === todayLocal;
    const sessionDate = isToday
      ? new Date().toISOString()
      : new Date(`${form.date}T12:00:00`).toISOString();

    const data = {
      date: sessionDate,
      tz_offset_minutes: new Date().getTimezoneOffset(),
      duration: parseInt(form.duration, 10),
      stance: form.stance,
      classFormat: form.classFormat,
      focusModes: form.focusModes,
      focus: form.focusModes.join(', '),  // back-compat shorthand
      intent: form.intent.trim(),
      reflection: form.reflection.trim(),
      techniques: form.techniques.filter((t) => t.name || t.subgroup),
      combinations: form.combinations.filter((c) => c.sequence || c.label),
      sparring: showSparring ? form.sparring : null,
      coachFeedback: form.coachFeedback.filter((c) => c.topic || c.note),
      weight: form.weight,
      matches: showMatchBlock ? form.matches.filter((m) => m.opponent) : [],
      injuryConcerns: form.injuryConcerns.filter((j) => j.area || j.note),
      notes: form.notes.trim(),
      attachments: form.attachments,
    };
    onSave(data);
    if (!isEditMode) clearDraft();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={() => { clearDraft(); onClose(); }} data-testid="boxing-log-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-enf-surface-deep border border-enf-surface-border rounded-xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-enf-surface-border sticky top-0 bg-enf-surface-deep z-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-semibold text-enf-text-muted">{isEditMode ? 'Edit session' : 'Log session'} · Boxing</div>
            <div className="font-display text-2xl tracking-wide leading-none mt-1 text-enf-text-primary">Combatives Journal</div>
          </div>
          <button type="button" onClick={() => { clearDraft(); onClose(); }} className="p-2 hover:bg-enf-surface-elevated rounded-lg" data-testid="boxing-close">
            <X className="w-4 h-4 text-enf-text-muted" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Draft restored */}
          {restoredFromDraft && (
            <div className="px-3 py-2 bg-enf-role-info/10 border border-enf-role-info/40 rounded text-xs text-enf-text-secondary" data-testid="boxing-restored-from-draft">
              Restored your in-progress entry from {formatTimeAgo(restoredFromDraft)}.
            </div>
          )}

          {/* Date + Duration */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-enf-text-muted mb-1">Date</label>
              <input type="date" value={form.date} onChange={(e) => setField('date', e.target.value)} max={todayLocal}
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded text-sm" data-testid="boxing-date" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-enf-text-muted mb-1">Duration (min)</label>
              <input type="number" min={1} value={form.duration} onChange={(e) => setField('duration', e.target.value)}
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded text-sm" data-testid="boxing-duration" />
            </div>
          </div>

          {/* Stance + Class format */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-enf-text-muted mb-1">Stance</label>
              <select value={form.stance} onChange={(e) => setField('stance', e.target.value)}
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded text-sm" data-testid="boxing-stance">
                {BOXING_STANCES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-enf-text-muted mb-1">Class format</label>
              <select value={form.classFormat} onChange={(e) => setField('classFormat', e.target.value)}
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded text-sm" data-testid="boxing-classformat">
                {BOXING_CLASS_FORMATS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
          </div>

          {/* Focus modes */}
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-enf-text-muted mb-2">Focus</label>
            <MultiSelectPills
              options={BOXING_FOCUS_MODES}
              value={form.focusModes}
              onChange={(v) => setField('focusModes', v)}
              testIdPrefix="boxing-focus"
            />
          </div>

          {/* Intent */}
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-enf-text-muted mb-1">Intent for today (optional)</label>
            <input type="text" value={form.intent} onChange={(e) => setField('intent', e.target.value)}
              placeholder="e.g., Cleaner 1-2 to the body; stop dropping my lead hand"
              className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded text-sm" data-testid="boxing-intent" />
          </div>

          {/* Techniques */}
          <CollapsibleSection title="Techniques worked" testId="boxing-section-techniques" defaultOpen>
            <div className="space-y-2">
              {form.techniques.map((t, i) => (
                <BoxingTechniqueRow
                  key={i}
                  index={i}
                  value={t}
                  subgroupOptions={boxingSubgroupsForGroup(t.group)}
                  techniqueOptions={boxingTechniquesFor(t.group, t.subgroup)}
                  onChange={(patch) => setTechAt(i, patch)}
                  onRemove={() => removeTechAt(i)}
                />
              ))}
              <button type="button" onClick={() => setField('techniques', [...form.techniques, newTechnique()])}
                className="w-full px-3 py-2 bg-enf-surface-elevated hover:bg-enf-surface-elevated border border-enf-surface-border rounded text-xs font-semibold flex items-center justify-center gap-2"
                data-testid="boxing-add-technique">
                <Plus className="w-3 h-3" /> Add technique
              </button>
            </div>
          </CollapsibleSection>

          {/* Combinations */}
          <CollapsibleSection title="Combinations" testId="boxing-section-combos">
            <div className="space-y-2">
              {form.combinations.map((c, i) => (
                <div key={i} className="bg-enf-surface-mid border border-enf-surface-border rounded p-3 space-y-2" data-testid={`boxing-combo-row-${i}`}>
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" value={c.sequence} onChange={(e) => setComboAt(i, { sequence: e.target.value })}
                      placeholder='Sequence — e.g. "1-2-3-2"'
                      className="px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs font-mono"
                      data-testid={`boxing-combo-sequence-${i}`} />
                    <input type="number" min={0} value={c.reps} onChange={(e) => setComboAt(i, { reps: e.target.value })}
                      placeholder="Reps"
                      className="px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
                      data-testid={`boxing-combo-reps-${i}`} />
                  </div>
                  <input type="text" value={c.label} onChange={(e) => setComboAt(i, { label: e.target.value })}
                    placeholder='Label / words — e.g. "jab-cross-hook-cross"'
                    className="w-full px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
                    data-testid={`boxing-combo-label-${i}`} />
                  <textarea value={c.notes} onChange={(e) => setComboAt(i, { notes: e.target.value })}
                    placeholder="Notes — what worked, what didn't, who you drilled it with"
                    rows={1}
                    className="w-full px-2 py-1.5 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
                    data-testid={`boxing-combo-notes-${i}`} />
                  <button type="button" onClick={() => setField('combinations', form.combinations.filter((_, idx) => idx !== i))}
                    className="text-xs text-enf-text-muted hover:text-enf-accent-fg" data-testid={`boxing-combo-remove-${i}`}>
                    Remove combo
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => setField('combinations', [...form.combinations, newCombo()])}
                className="w-full px-3 py-2 bg-enf-surface-elevated hover:bg-enf-surface-elevated border border-enf-surface-border rounded text-xs font-semibold flex items-center justify-center gap-2"
                data-testid="boxing-add-combo">
                <Plus className="w-3 h-3" /> Add combo
              </button>
            </div>
          </CollapsibleSection>

          {/* Sparring (conditional) */}
          {showSparring && (
            <CollapsibleSection title="Sparring" testId="boxing-section-sparring" defaultOpen>
              <BoxingSparringBlock
                value={form.sparring || { ...newSparring(), gloveWeightOz: gloveWeightDefault }}
                onChange={(v) => setField('sparring', v)}
              />
            </CollapsibleSection>
          )}

          {/* Coach feedback */}
          <CollapsibleSection title="Coach feedback" testId="boxing-section-coach">
            <div className="space-y-2">
              {form.coachFeedback.map((c, i) => (
                <div key={i} className="bg-enf-surface-mid border border-enf-surface-border rounded p-3 space-y-2" data-testid={`boxing-coach-row-${i}`}>
                  <div className="flex items-center gap-2">
                    <select value={c.topic} onChange={(e) => setCoachAt(i, { topic: e.target.value })}
                      className="px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
                      data-testid={`boxing-coach-topic-${i}`}>
                      <option value="">(topic)</option>
                      {BOXING_COACH_TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <button type="button" onClick={() => setField('coachFeedback', form.coachFeedback.filter((_, idx) => idx !== i))}
                      className="ml-auto text-enf-text-muted hover:text-enf-accent-fg" data-testid={`boxing-coach-remove-${i}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  <textarea value={c.note} onChange={(e) => setCoachAt(i, { note: e.target.value })}
                    placeholder="Coach's note — keywords now, expand later"
                    rows={2}
                    className="w-full px-2 py-1.5 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
                    data-testid={`boxing-coach-note-${i}`} />
                </div>
              ))}
              <button type="button" onClick={() => setField('coachFeedback', [...form.coachFeedback, newCoachNote()])}
                className="w-full px-3 py-2 bg-enf-surface-elevated hover:bg-enf-surface-elevated border border-enf-surface-border rounded text-xs font-semibold flex items-center justify-center gap-2"
                data-testid="boxing-add-coach">
                <Plus className="w-3 h-3" /> Add coach note
              </button>
            </div>
          </CollapsibleSection>

          {/* Matches (conditional) */}
          {showMatchBlock && (
            <CollapsibleSection title="Matches" testId="boxing-section-matches" defaultOpen>
              <div className="space-y-2">
                {form.matches.map((m, i) => (
                  <div key={i} className="bg-enf-surface-mid border border-enf-surface-border rounded p-3 space-y-2" data-testid={`boxing-match-row-${i}`}>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="text" value={m.opponent} onChange={(e) => setMatchAt(i, { opponent: e.target.value })}
                        placeholder="Opponent" className="px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
                        data-testid={`boxing-match-opponent-${i}`} />
                      <select value={m.result} onChange={(e) => setMatchAt(i, { result: e.target.value })}
                        className="px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
                        data-testid={`boxing-match-result-${i}`}>
                        {BOXING_MATCH_RESULTS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                      </select>
                    </div>
                    <input type="text" value={m.finish} onChange={(e) => setMatchAt(i, { finish: e.target.value })}
                      placeholder='Score / Finish (optional) — e.g. "TKO R2", "decision", "majority draw"'
                      className="w-full px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
                      data-testid={`boxing-match-finish-${i}`} />
                    <textarea value={m.notes} onChange={(e) => setMatchAt(i, { notes: e.target.value })}
                      placeholder="Key moments, what coach said after, what to work on"
                      rows={2} className="w-full px-2 py-1.5 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
                      data-testid={`boxing-match-notes-${i}`} />
                    <button type="button" onClick={() => setField('matches', form.matches.filter((_, idx) => idx !== i))}
                      className="text-xs text-enf-text-muted hover:text-enf-accent-fg" data-testid={`boxing-match-remove-${i}`}>
                      Remove match
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => setField('matches', [...form.matches, newMatch()])}
                  className="w-full px-3 py-2 bg-enf-surface-elevated hover:bg-enf-surface-elevated border border-enf-surface-border rounded text-xs font-semibold flex items-center justify-center gap-2"
                  data-testid="boxing-add-match">
                  <Plus className="w-3 h-3" /> Add match
                </button>
              </div>
            </CollapsibleSection>
          )}

          {/* Weight log — collapsed by default per founder */}
          <CollapsibleSection title="Weight & hydration" testId="boxing-section-weight" icon={<Scale className="w-3 h-3 text-enf-text-muted" />}>
            <BoxingWeightBlock
              value={form.weight || newWeight()}
              onChange={(v) => setField('weight', v)}
              flag={weightFlag}
            />
          </CollapsibleSection>

          {/* Injury concerns */}
          <CollapsibleSection title="Injury concerns" testId="boxing-section-injury">
            <div className="space-y-2">
              {form.injuryConcerns.map((j, i) => (
                <div key={i} className="bg-enf-surface-mid border border-enf-surface-border rounded p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" value={j.area} onChange={(e) => setInjuryAt(i, { area: e.target.value })}
                      placeholder="Area (e.g., hand, wrist, jaw, ribs)"
                      className="px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
                      data-testid={`boxing-injury-area-${i}`} />
                    <input type="number" min={1} max={5} value={j.severity} onChange={(e) => setInjuryAt(i, { severity: parseInt(e.target.value, 10) || 1 })}
                      placeholder="Severity 1-5"
                      className="px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
                      data-testid={`boxing-injury-severity-${i}`} />
                  </div>
                  <textarea value={j.note} onChange={(e) => setInjuryAt(i, { note: e.target.value })}
                    placeholder="What happened / what's tight" rows={2}
                    className="w-full px-2 py-1.5 bg-enf-surface-deep border border-enf-surface-border rounded text-xs" />
                  <button type="button" onClick={() => setField('injuryConcerns', form.injuryConcerns.filter((_, idx) => idx !== i))}
                    className="text-xs text-enf-text-muted hover:text-enf-accent-fg">Remove</button>
                </div>
              ))}
              <button type="button" onClick={() => setField('injuryConcerns', [...form.injuryConcerns, newInjury()])}
                className="w-full px-3 py-2 bg-enf-surface-elevated hover:bg-enf-surface-elevated border border-enf-surface-border rounded text-xs font-semibold flex items-center justify-center gap-2"
                data-testid="boxing-add-injury">
                <Plus className="w-3 h-3" /> Log an injury concern
              </button>
            </div>
          </CollapsibleSection>

          {/* Reflection */}
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-enf-text-muted mb-1">Reflection (what stuck with you)</label>
            <textarea value={form.reflection} onChange={(e) => setField('reflection', e.target.value)}
              placeholder="What you learned, where you got stuck, what to work on next"
              rows={3} className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded text-sm"
              data-testid="boxing-reflection" />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-enf-text-muted mb-1">Additional notes</label>
            <textarea value={form.notes} onChange={(e) => setField('notes', e.target.value)} rows={2}
              className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded text-sm"
              data-testid="boxing-notes" />
          </div>

          {/* Attachments */}
          <CollapsibleSection title="Photos / video / links" testId="boxing-section-attachments">
            <div className="space-y-3">
              <MediaCaptureMenu onFiles={addAttachmentFiles} recordingEtiquette />
              <PasteUrlInput onUrl={addAttachmentUrl} />
              {form.attachments.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {form.attachments.map((a, i) => (
                    <AttachmentTile key={i} attachment={a} onRemove={() => removeAttachment(i)} />
                  ))}
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* Error */}
          {error && (
            <div className="px-3 py-2 bg-enf-role-danger/10 border border-enf-role-danger/40 rounded text-xs text-enf-accent-fg flex items-center gap-2" data-testid="boxing-error">
              <AlertCircle className="w-3 h-3" /> {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-enf-surface-border bg-enf-surface-deep sticky bottom-0 flex items-center gap-2 z-10">
          <button type="button" onClick={() => { clearDraft(); onClose(); }}
            className="px-4 py-2 text-sm text-enf-text-secondary hover:text-enf-text-primary" data-testid="boxing-cancel">
            Cancel
          </button>
          <button type="button" onClick={handleSave}
            className="flex-1 px-5 py-2.5 bg-enf-accent hover:bg-enf-accent-hover text-enf-text-primary rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
            data-testid="boxing-save">
            {isEditMode ? 'Save changes' : 'Save session'}
          </button>
        </div>
      </div>
    </div>
  );
};


// --- Inline technique row ---
const BoxingTechniqueRow = ({ index, value, subgroupOptions, techniqueOptions, onChange, onRemove }) => {
  const set = (patch) => onChange(patch);
  return (
    <div className="bg-enf-surface-mid border border-enf-surface-border rounded p-3 space-y-2" data-testid={`boxing-technique-${index}`}>
      <div className="grid grid-cols-3 gap-2">
        <select value={value.group} onChange={(e) => set({ group: e.target.value, subgroup: '', name: '' })}
          className="px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs" data-testid={`boxing-technique-group-${index}`}>
          {BOXING_GROUPS.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
        </select>
        <select value={value.subgroup} onChange={(e) => set({ subgroup: e.target.value, name: '' })}
          className="px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs" data-testid={`boxing-technique-subgroup-${index}`}>
          <option value="">(subgroup)</option>
          {subgroupOptions.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={value.mastery} onChange={(e) => set({ mastery: e.target.value })}
          className="px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs" data-testid={`boxing-technique-mastery-${index}`}>
          <option value="">(mastery)</option>
          {BOXING_MASTERY_LEVELS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <input type="text" value={value.name} onChange={(e) => set({ name: e.target.value })}
          list={`boxing-tech-options-${index}`}
          placeholder="Technique (pick a suggestion or type your own)"
          className="flex-1 px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
          data-testid={`boxing-technique-name-${index}`} />
        <datalist id={`boxing-tech-options-${index}`}>
          {techniqueOptions.map((t) => <option key={t} value={t} />)}
        </datalist>
        <input type="number" min={0} value={value.reps} onChange={(e) => set({ reps: e.target.value })}
          placeholder="Reps" className="w-20 px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
          data-testid={`boxing-technique-reps-${index}`} />
        <button type="button" onClick={onRemove} className="text-enf-text-muted hover:text-enf-accent-fg" data-testid={`boxing-technique-remove-${index}`}>
          <X className="w-3 h-3" />
        </button>
      </div>
      <textarea value={value.note} onChange={(e) => set({ note: e.target.value })}
        placeholder="Detail / what worked / what didn't" rows={1}
        className="w-full px-2 py-1.5 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
        data-testid={`boxing-technique-note-${index}`} />
    </div>
  );
};


// --- Sparring block (rounds-based) ---
const BoxingSparringBlock = ({ value, onChange }) => {
  const set = (patch) => onChange({ ...value, ...patch });
  const addRoundNote = () => set({ rounds: [...(value.rounds || []), newRoundNote()] });
  const setRoundAt = (i, patch) => set({
    rounds: (value.rounds || []).map((r, idx) => idx === i ? { ...r, ...patch } : r),
  });
  const removeRoundAt = (i) => set({ rounds: (value.rounds || []).filter((_, idx) => idx !== i) });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <NumCell k="roundsCompleted" label="Rounds" value={value} set={set} testId="boxing-spar-rounds" />
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.15em] text-enf-text-muted mb-1">Round length (sec)</span>
          <input type="number" min={30} step={30} value={value.roundLengthSec ?? 180}
            onChange={(e) => set({ roundLengthSec: parseInt(e.target.value, 10) || 180 })}
            className="w-full px-2 py-1 bg-enf-surface-mid border border-enf-surface-border rounded text-xs"
            data-testid="boxing-spar-roundlen" />
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.15em] text-enf-text-muted mb-1">Gloves (oz)</span>
          <select value={value.gloveWeightOz ?? ''} onChange={(e) => set({ gloveWeightOz: e.target.value })}
            className="w-full px-2 py-1 bg-enf-surface-mid border border-enf-surface-border rounded text-xs" data-testid="boxing-spar-gloves">
            <option value="">(select)</option>
            {BOXING_GLOVE_OPTIONS.map((g) => <option key={g} value={g}>{g} oz</option>)}
          </select>
        </label>
        <label className="block sm:col-span-2">
          <span className="block text-[10px] uppercase tracking-[0.15em] text-enf-text-muted mb-1">Partner experience</span>
          <select value={value.partnerExperienceLevel ?? ''} onChange={(e) => set({ partnerExperienceLevel: e.target.value })}
            className="w-full px-2 py-1 bg-enf-surface-mid border border-enf-surface-border rounded text-xs" data-testid="boxing-spar-partnerlevel">
            <option value="">(select)</option>
            {BOXING_PARTNER_LEVELS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.15em] text-enf-text-muted mb-1">Intensity</span>
          <select value={value.intensityRating ?? 3} onChange={(e) => set({ intensityRating: parseInt(e.target.value, 10) })}
            className="w-full px-2 py-1 bg-enf-surface-mid border border-enf-surface-border rounded text-xs" data-testid="boxing-spar-intensity">
            {BOXING_FELT_SCALE.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </label>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <NumCell k="cleanLandsScored" label="Clean lands ✓" value={value} set={set} testId="boxing-spar-lands-scored" />
        <NumCell k="cleanLandsConceded" label="Clean lands ✗" value={value} set={set} testId="boxing-spar-lands-conceded" />
        <NumCell k="knockdownsScored" label="Knockdowns ✓" value={value} set={set} testId="boxing-spar-kd-scored" />
        <NumCell k="knockdownsConceded" label="Knockdowns ✗" value={value} set={set} testId="boxing-spar-kd-conceded" />
      </div>

      {/* Optional per-round notes — on-demand */}
      <div className="space-y-2 pt-2 border-t border-enf-surface-border">
        <div className="text-[10px] uppercase tracking-[0.15em] text-enf-text-muted">Per-round notes (optional)</div>
        {(value.rounds || []).map((r, i) => (
          <div key={i} className="grid grid-cols-[60px_1fr_auto] gap-2 items-start" data-testid={`boxing-spar-round-${i}`}>
            <input type="number" min={1} value={r.roundNum} onChange={(e) => setRoundAt(i, { roundNum: e.target.value })}
              placeholder="R#" className="px-2 py-1 bg-enf-surface-mid border border-enf-surface-border rounded text-xs"
              data-testid={`boxing-spar-round-num-${i}`} />
            <textarea value={r.note} onChange={(e) => setRoundAt(i, { note: e.target.value })}
              placeholder="What happened this round"
              rows={1} className="px-2 py-1.5 bg-enf-surface-mid border border-enf-surface-border rounded text-xs"
              data-testid={`boxing-spar-round-note-${i}`} />
            <button type="button" onClick={() => removeRoundAt(i)} className="text-enf-text-muted hover:text-enf-accent-fg p-1"
              data-testid={`boxing-spar-round-remove-${i}`}>
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button type="button" onClick={addRoundNote}
          className="text-xs text-enf-text-muted hover:text-enf-text-primary flex items-center gap-1"
          data-testid="boxing-spar-add-round">
          <Plus className="w-3 h-3" /> Add round note
        </button>
      </div>

      <textarea value={value.note ?? ''} onChange={(e) => set({ note: e.target.value })}
        placeholder="Overall sparring note — what your partner did, what coach saw"
        rows={2} className="w-full px-2 py-1.5 bg-enf-surface-mid border border-enf-surface-border rounded text-xs"
        data-testid="boxing-spar-note" />
    </div>
  );
};

// --- Weight block (with anti-shame guardrails) ---
const BoxingWeightBlock = ({ value, onChange, flag }) => {
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className="space-y-3">
      <div className="px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded text-xs text-enf-text-muted flex items-start gap-2" data-testid="boxing-weight-disclaimer">
        <Info className="w-3 h-3 mt-0.5 flex-shrink-0 text-enf-role-info" />
        <span>
          We&apos;re a notebook, not a doctor. Logging weight here helps you spot patterns, not plan a cut. For cut planning, work with your coach or a sports dietitian — start with these references: {BOXING_WEIGHT_REFERENCES.map((r, i) => (
            <React.Fragment key={r.url}>
              {i > 0 && ' · '}
              <a href={r.url} target="_blank" rel="noopener noreferrer" className="underline hover:text-enf-text-primary">{r.label}</a>
            </React.Fragment>
          ))}.
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.15em] text-enf-text-muted mb-1">Today&apos;s weight (lb)</span>
          <input type="number" step="0.1" value={value.scaleWeightLb} onChange={(e) => set({ scaleWeightLb: e.target.value })}
            className="w-full px-2 py-1 bg-enf-surface-mid border border-enf-surface-border rounded text-xs" data-testid="boxing-weight-scale" />
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.15em] text-enf-text-muted mb-1">Target class (lb)</span>
          <input type="number" value={value.targetClassLb} onChange={(e) => set({ targetClassLb: e.target.value })}
            className="w-full px-2 py-1 bg-enf-surface-mid border border-enf-surface-border rounded text-xs" data-testid="boxing-weight-target" />
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.15em] text-enf-text-muted mb-1">Days to weigh-in</span>
          <input type="number" min={0} value={value.daysToWeighIn} onChange={(e) => set({ daysToWeighIn: e.target.value })}
            className="w-full px-2 py-1 bg-enf-surface-mid border border-enf-surface-border rounded text-xs" data-testid="boxing-weight-days" />
        </label>
        <label className="block col-span-2 sm:col-span-2">
          <span className="block text-[10px] uppercase tracking-[0.15em] text-enf-text-muted mb-1">Cut method</span>
          <select value={value.cutMethod} onChange={(e) => set({ cutMethod: e.target.value })}
            className="w-full px-2 py-1 bg-enf-surface-mid border border-enf-surface-border rounded text-xs" data-testid="boxing-weight-cutmethod">
            <option value="">(select)</option>
            {BOXING_CUT_METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.15em] text-enf-text-muted mb-1">Hydration</span>
          <select value={value.hydrationStatus} onChange={(e) => set({ hydrationStatus: e.target.value })}
            className="w-full px-2 py-1 bg-enf-surface-mid border border-enf-surface-border rounded text-xs" data-testid="boxing-weight-hydration">
            <option value="">(self-check)</option>
            {BOXING_HYDRATION.map((h) => <option key={h.id} value={h.id}>{h.label} — {h.help}</option>)}
          </select>
        </label>
      </div>
      {flag && (
        <div
          className={`px-3 py-2 rounded text-xs flex items-start gap-2 border ${
            flag.tone === 'urgent'
              ? 'bg-enf-role-danger/15 border-enf-role-danger/40 text-enf-accent-fg'
              : 'bg-enf-accent/10 border-enf-accent-fg/40 text-enf-text-primary'
          }`}
          data-testid="boxing-weight-flag"
        >
          <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>{flag.message}</span>
        </div>
      )}
      <textarea value={value.notes} onChange={(e) => set({ notes: e.target.value })}
        placeholder="How you are feeling, what you ate, energy level — anything to remember next week"
        rows={2} className="w-full px-2 py-1.5 bg-enf-surface-mid border border-enf-surface-border rounded text-xs"
        data-testid="boxing-weight-notes" />
    </div>
  );
};

export default BoxingLogDialog;
